/* eslint-disable @typescript-eslint/no-unused-expressions */
import React from 'react';
import { IonicButton, IonicInput, TermsCheckbox } from 'ionic-ui';
import Layout from './Layout';
import { SignUpPageProps } from './SignUp';
import 'flag-icons/css/flag-icons.min.css';
import { createUser } from '../../services/authentication/signup';
import { setTemporalSession } from '../../utils/tempSession';
import PasswordInput from '../../components/ui/PasswordInput';
import useIonicToast from '../../hooks/useIonicToast';
import { usePageLoader } from '../../context/pageLoader';
import PhoneNumberInput from '../../components/ui/PhoneNumberInput';
import { Controller, useFormContext } from 'react-hook-form';
import z from 'zod';
import { signupSchema } from '../../schemas/signupSchema';
import { baseURL } from '../../data/config';

const SignUp01: React.FC<SignUpPageProps> = ({ next }) => {
  const { setBusy } = usePageLoader();

  const { presentIonToast } = useIonicToast();
  const { getValues, trigger, ...formMethods } =
    useFormContext<z.infer<typeof signupSchema>>();

  const [termsAccepted, setTermsAccepted] = React.useState(false);

  // Handle form submission for step
  const handleSubmit = async () => {
    const isValid = await trigger([
      'first_name',
      'last_name',
      'email',
      'password',
      'confirm_password',
      'ref_code',
      'phone',
    ]);
    if (!isValid) return;
    if (!termsAccepted) {
      presentIonToast(
        'Please review and accept the Terms & Conditions to continue.',
        'error'
      );
      return;
    }

    const formData = getValues();

    try {
      setBusy(true);
      const requestBody = { ...formData };
      const res = await createUser(requestBody);
      if (res.code === 220) {
        presentIonToast(
          'You already have a swiftmedics account, try signin',
          'error'
        );
      }
      const temp_user = formData;
      setTemporalSession({ ...temp_user, isVerified: true });
      next();
    } catch (err: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      let msg: string = err.message;
      if (err?.status === 409) msg = 'Email or Phone number already in use';
      presentIonToast(msg, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      uiType="signup"
      description="Consult, Shop and Top up - All in one Place"
      step={1}
      totalStep={2}
    >
      <div className="flex flex-col gap-6 w-[100%]">
        {/* first name Input */}
        <Controller
          name="first_name"
          control={formMethods.control}
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              size="xlg"
              variant="filled"
              type="text"
              label="First Name*"
              aria-label="First Name"
              placeholder="Enter your first name"
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        {/* last name Input */}
        <Controller
          control={formMethods.control}
          name="last_name"
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              size="xlg"
              variant="filled"
              type="text"
              name="last_name"
              label="Last Name*"
              aria-label="Last Name"
              placeholder="Enter your last name"
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        {/* Email Input */}
        <Controller
          control={formMethods.control}
          name="email"
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              size="xlg"
              variant="filled"
              type="email"
              label="Official Email Address*"
              aria-label="Email Address"
              placeholder="Enter your official email address"
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        <div className="flex max-sm:flex-col gap-4">
          {/* Password Input */}
          <Controller
            control={formMethods.control}
            name="password"
            render={({ field, fieldState }) => (
              <PasswordInput
                {...field}
                containerClass="max-sm:flex-1"
                size="xlg"
                variant="filled"
                label="Password*"
                aria-label="Password"
                placeholder="XXXXXXXX"
                hint={fieldState.error?.message}
                hintClass="text-red-600 dark:text-red-600"
              />
            )}
          />

          {/* Confirm Password Input */}
          <Controller
            control={formMethods.control}
            name="confirm_password"
            render={({ field, fieldState }) => (
              <PasswordInput
                {...field}
                containerClass="max-sm:flex-1"
                size="xlg"
                variant="filled"
                label="Confirm Password*"
                aria-label="Confirm Password"
                placeholder="XXXXXXXX"
                hint={fieldState.error?.message}
                hintClass="text-red-600 dark:text-red-600"
              />
            )}
          />
        </div>

        {/* Phone Number Input */}
        <Controller
          control={formMethods.control}
          name="phone"
          render={({ field, fieldState }) => (
            <PhoneNumberInput
              {...field}
              size="xlg"
              variant="filled"
              type="tel"
              label="Phone number*"
              aria-label="Phone number"
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        {/* Referral Code Input */}
        <Controller
          control={formMethods.control}
          name="ref_code"
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              size="xlg"
              variant="filled"
              type="text"
              label="Referral Code"
              aria-label="Referral Code"
              placeholder="Have a referral code?"
              hint={fieldState.error?.message}
              hintClass="text-red-600 dark:text-red-600"
            />
          )}
        />

        {/* Terms & Conditions agreement */}
        <TermsCheckbox
          audience="store"
          baseURL={baseURL}
          checked={termsAccepted}
          onChange={setTermsAccepted}
        />

        {/* Buttons */}
        <div className="flex justify-between mt-8">
          <IonicButton
            variant="outline"
            size="xlg"
            disabled={true}
            className={`text-primary`}
          >
            Previous
          </IonicButton>

          <div className="flex justify-between gap-4">
            <IonicButton
              variant="ghost"
              disabled={true}
              size="xlg"
              type="button"
              className={`text-primary dark:text-secondary cursor-no-drop`}
            >
              Skip
            </IonicButton>
            <IonicButton
              type="button"
              onClick={handleSubmit}
              size="xlg"
              disabled={!termsAccepted}
            >
              Next
            </IonicButton>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SignUp01;
